import { css } from '@emotion/react'
import { StudentLecture, type StudentLectureDtoT } from '@mathflat/domain/@entities/StudentLecture'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import clsx from 'clsx'
import { differenceInCalendarDays, format, startOfDay } from 'date-fns'
import { useCallback, useEffect, useRef } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import { tJosa } from '~/@app/i18n'
import { routeName } from '~/@common/constants'
import modalService from '~/@common/services/modal.service'
import { textEllipsis, typo } from '~/@common/styles'
import Modal from '~/@common/ui/modal/Modal'

const StatusText = ({ status }: { status: StudentLectureDtoT.AssignmentStatus }) => {
  if (status === 'IN_PROGRESS_EXPIRED') {
    return (
      <>
        학습중
        <br />
        (기간만료)
      </>
    )
  }
  return StudentLecture.ASSIGNMENT_STATUS_TEXT[status]
}

const formatDate = (dateString: string) => format(new Date(dateString), 'yy.MM.dd')

const getDDayLabel = (dueAt: string): string | null => {
  const diff = differenceInCalendarDays(startOfDay(new Date(dueAt)), startOfDay(new Date()))
  if (diff === 0) return 'D-0'
  if (diff === 1) return 'D-1'
  return null
}

type Props = {
  assignments: StudentLectureDtoT.AssignmentResponse[]
  onLoadMore?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  isFetched?: boolean
  isFiltered?: boolean
}

const AssignedLectureTable = ({
  assignments,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  isFetched = false,
  isFiltered = false,
}: Props) => {
  const sentinelRef = useRef<HTMLDivElement>(null)

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (!entries[0]) return
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        onLoadMore?.()
      }
    },
    [hasNextPage, isFetchingNextPage, onLoadMore],
  )

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(handleIntersect, { threshold: 0 })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [handleIntersect])
  const navigate = useNavigate()

  const handleClickLecture = (assignment: StudentLectureDtoT.AssignmentResponse) => {
    if (assignment.status === 'EXPIRED' || assignment.status === 'IN_PROGRESS_EXPIRED') {
      modalService.openModal(
        <Modal.Alert
          confirm={{
            onClick: modalService.closeAllModal,
          }}
        >
          시청기한이 만료된 {tJosa('강의')}입니다.
        </Modal.Alert>,
        { modalName: 'expired-lecture-notice' },
      )
      return
    }

    navigate(
      generatePath(routeName.student.assignedLectureDetail, {
        lectureId: String(assignment.lectureId),
        assignmentId: String(assignment.assignmentId),
      }),
    )
  }

  return (
    <div css={S.container}>
      <div css={S.tableHeader}>
        <div css={S.colDate}>{tJosa('출제')}일</div>
        <div css={S.colThumbnail} />
        <div css={S.colChapter}>
          <span>단원명</span>
        </div>
        <div css={S.colTitle}>{tJosa('강의')}명</div>
        <div css={S.colDeadline}>시청기한</div>
        <div css={S.colStatus}>상태</div>
        <div css={S.colAction}>학습하기</div>
      </div>
      <div css={S.tableBody}>
        {isFetched && assignments.length === 0 ? (
          <div css={S.emptyState}>
            <img
              css={S.emptyImage}
              src={s3URL.student('images/airplain_premium.svg')}
              alt={`${tJosa('출제')}된 ${tJosa('강의')} 없음`}
            />
            <p css={S.emptyText}>
              {isFiltered
                ? '조건에 맞는 결과가 없습니다.'
                : `선생님이 ${tJosa('출제')}한 ${tJosa('강의', '이/가')} 없습니다.`}
            </p>
          </div>
        ) : (
          <>
            {assignments.map((assignment) => (
              <div css={S.row} key={assignment.assignmentId}>
                <div css={S.colDate}>{formatDate(assignment.assignedAt)}</div>
                <div css={S.colThumbnail}>
                  {assignment.thumbnailUrl && (
                    <img
                      src={assignment.thumbnailUrl}
                      alt={`${tJosa('강의')} 썸네일`}
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                </div>
                <div css={[S.colChapter, S.chapterCell]}>
                  <span>{assignment.bigChapterName}</span>
                </div>
                <div css={S.colTitle}>{assignment.lectureTitle}</div>
                <div css={S.colDeadline}>
                  {!assignment.dueAt ? (
                    <span>제한없음</span>
                  ) : (
                    <>
                      <span>
                        {formatDate(assignment.assignedAt)} - {formatDate(assignment.dueAt)}
                      </span>
                      {getDDayLabel(assignment.dueAt) && (
                        <span css={S.dDayBadge}>{getDDayLabel(assignment.dueAt)}</span>
                      )}
                    </>
                  )}
                </div>
                <div css={S.colStatus} className={clsx('table-col', assignment.status)}>
                  <StatusText status={assignment.status} />
                </div>
                <div css={S.colAction}>
                  <button
                    css={S.button}
                    type="button"
                    onClick={() => handleClickLecture(assignment)}
                  >
                    학습하기
                  </button>
                </div>
              </div>
            ))}
            <div ref={sentinelRef} css={S.sentinel} />
          </>
        )}
      </div>
    </div>
  )
}

export default AssignedLectureTable

const S = {
  container: css`
    display: flex;
    flex-direction: column;
    background: var(--White);
    border-radius: var(--Radius-300);
    overflow: hidden;
    height: 100%;
  `,
  tableBody: css`
    flex: 1;
    overflow-y: auto;
  `,
  tableHeader: css`
    display: flex;
    align-items: center;
    padding: 16px 14px;
    background: var(--Colors-Gray-100);
    font-weight: 700;
    ${typo.body02};
    color: var(--Colors-Gray-600);
  `,
  row: css`
    display: flex;
    align-items: center;
    padding: 20px 14px;
    border-top: 1px solid var(--Colors-Gray-200);
    ${typo.body02};
    color: var(--Colors-Gray-900);
  `,
  colDate: css`
    width: 100px;
    text-align: center;
    flex-shrink: 0;
  `,
  colThumbnail: css`
    width: 53px;
    margin-right: 16px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: var(--Radius-100);
      background: var(--Colors-Gray-200);
    }
  `,
  colChapter: css`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
    width: 160px;
  `,
  colTitle: css`
    flex: 1;
    padding: 0 20px;
    ${textEllipsis(1)}
  `,
  colDeadline: css`
    width: 160px;
    text-align: center;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  `,
  colStatus: css`
    width: 120px;
    text-align: center;
    flex-shrink: 0;
    ${typo.body02};

    &.table-col {
      color: var(--Colors-Gray-500);
      font-weight: 500;
    }

    &.NOT_STARTED {
      color: var(--Colors-Blue-500);
    }
    &.IN_PROGRESS {
      color: var(--Colors-Gray-800);
      font-weight: 400;
    }
  `,
  colAction: css`
    width: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `,
  chapterCell: css`
    span {
      width: 160px;
    }
  `,
  dDayBadge: css`
    color: var(--White);
    ${typo.caption02};
    font-weight: 700;
    background-color: var(--Colors-Gray-900);
    border-radius: var(--Radius-100);
    padding: 2px 4px;
  `,
  emptyState: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 0;
    height: 100%;
  `,
  emptyImage: css`
    width: 150px;
    height: 150px;
    object-fit: contain;
  `,
  emptyText: css`
    ${typo.body01};
    font-weight: 600;
    color: var(--Colors-Gray-700);
  `,

  button: css`
    display: flex;
    width: 100px;
    height: 36px;
    padding: 0 16px;
    align-items: center;
    justify-content: center;
    border-radius: var(--Radius-100);
    border: 1px solid var(--Colors-Gray-400);
    background: var(--Colors-Gray-100);

    &[disabled] {
      color: var(--Colors-Gray-500);
      border-color: var(--Colors-Gray-400);
      background: var(--Colors-Gray-100);
      cursor: not-allowed;
    }
  `,
  sentinel: css`
    height: 1px;
  `,
}
